<template>
    <div class="tw-w-full">

            <div class="tw-table tw-w-full">
                <div class="tw-h-4"></div>
                <div class="tw-table-row tw-pt-0">
                    <div class="dotted"></div>
                </div>
                <div class="tw-table-cell tw-align-middle tw-h-[100px]">
                    <div class="tw-text-center"><h3>{{ __('purchase.order_details') }}</h3></div>
                </div>
                <div class="tw-table-row tw-pt-0">
                    <div class="dotted"></div>
                </div>
            </div>

            <Form @submit="formWillSubmit">
                <table class="tw-w-full tw-mb-4">
                    <tbody>
                        <tr>
                            <td class="tw-w-1/2"></td>
                            <td class="tw-w-1/4 tw-text-right" colspan="2"><b>{{__('purchase.monthly_fee')}}</b></td>
                        </tr>
                        <tr></tr>
                        <tr class="tw-mt-1">
                            <td class="tw-w-1/2">{{ __('purchase.workperks_licenses') }}</td>
                            <td class="tw-w-1/4 tw-text-center">
                                <Field
                                    name="quantity"
                                    type="number"
                                    v-model="quantity"
                                    class="tw-w-full tw-px-3 tw-py-2 tw-border-none tw-rounded-md tw-text-center"
                                    :class="{ 'tw-border tw-border-red-500': showQuantityError }"
                                    step="1"
                                    min="1"
                                    @blur="updateInvoice"
                                    :disabled="currentComponentIndex === 0"
                                />
                                <span v-show="showQuantityError" class="tw-text-red-500 tw-text-sm">{{ quantityErrorMessage }}</span>
                            </td>
                            <td class="tw-w-1/4 tw-text-right">{{ formatCurrency(totalPrice) }}</td>
                        </tr>
                        <tr class="tw-mt-1" v-if="freeLicenses">
                            <td class="tw-w-1/2 tw-text-green-600 tw-font-bold">{{ __('purchase.free_licenses_title') }}</td>
                            <td class="tw-w-1/4 tw-text-center">{{ freeLicenses }}</td>
                            <td class="tw-w-1/4 tw-text-right tw-text-red-600 tw-font-bold">-{{ formatCurrency(freeLicenses * actualPrice) }}</td>
                        </tr>
                        <template v-if="hasPromotion">
                        <tr>
                            <td class="tw-w-1/2">{{ referralName }}</td>
                            <td class="tw-w-1/4 tw-text-center">{{ discountPercentage }}% off</td>
                            <td class="tw-w-1/4 tw-text-right tw-text-red-600">-{{ formatCurrency(totalDiscount) }}</td>
                        </tr>
                        <tr>
                            <td class="tw-w-1/2 tw-text-xs">{{ __('purchase.promo_code_applied') }}</td>
                        </tr>
                        </template>
                        <tr v-if="!hasPromotion">
                            <td colspan="3">
                                <div class="tw-flex tw-mb-3">
                                    <Field
                                        name="promotion_code"
                                        type="text"
                                        v-model="promotionCode"
                                        :placeholder="__('purchase.promo_code_placeholder')"
                                        class="tw-flex-grow tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-l"
                                        :class="{ 'tw-border-red-500': showPromoCodeError }"
                                    />
                                    <div class="tw-flex">
                                        <button class="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-r hover:tw-bg-blue-700" type="button" @click.prevent="applyPromoCode">+</button>
                                    </div>
                                </div>
                                <span v-show="showPromoCodeError" class="tw-text-red-500 tw-text-sm tw-text-left">{{ promoCodeErrorMessage }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Form>

            <div class="tw-h-[100px]"></div>

            <!-- Invoice Summary Section -->
            <hr class="tw-border-t tw-border-solid tw-border-gray-800 tw-my-3"/>
            <div class="tw-flex tw-flex-wrap">
                <div class="tw-w-2/3 tw-mb-2"><h3>{{ __('purchase.subtotal') }}</h3></div>
                <div class="tw-w-1/3 tw-text-right tw-mb-2">{{ formatCurrency(subtotal) }}</div>
            </div>
            
            <!-- Tax Details - Only show if tax is charged -->
            <template v-if="taxCharged > 0">
                <div class="tw-flex tw-flex-wrap" v-for="(rate, name) in taxDetails" :key="name">
                    <div class="tw-w-2/3 tw-mb-2"><h3>{{ name }}</h3></div>
                    <div class="tw-w-1/3 tw-text-right">{{ formatCurrency(rate * subtotal) }}</div>
                </div>
                <hr class="tw-border-t tw-border-solid tw-border-gray-800 tw-my-3" />
                <div class="tw-flex tw-flex-wrap">
                    <div class="tw-w-2/3 tw-mb-2"><h3>{{ __('purchase.total') }}</h3></div>
                    <div class="tw-w-1/3 tw-text-right tw-mb-2">{{ formatCurrency(netPrice) }}</div>
                </div>
                <hr class="tw-border-t tw-border-solid tw-border-gray-800 tw-my-3"/>
            </template>
            
            <!-- Always show total when no tax -->
            <template v-else>
                <hr class="tw-border-t tw-border-solid tw-border-gray-800 tw-my-3" />
                <div class="tw-flex tw-flex-wrap">
                    <div class="tw-w-2/3 tw-mb-2"><h3>{{ __('purchase.total') }}</h3></div>
                    <div class="tw-w-1/3 tw-text-right tw-mb-2">{{ formatCurrency(subtotal) }}</div>
                </div>
                <hr class="tw-border-t tw-border-solid tw-border-gray-800 tw-my-3"/>
            </template>

            <div class="tw-flex tw-flex-wrap">
                <div class="tw-w-full"><p class="tw-text-gray-500 tw-text-sm">{{ __('purchase.moneyback_guarantee') }}</p></div>
            </div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'Invoice'
}
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, inject } from 'vue';
import { useForm, useField, Form, Field } from 'vee-validate';
import { __ } from 'lib/Utilities';

// Define types for props
interface Props {
    numLicenses?: number;
    freeLicenses?: number;
    taxPercentage?: number;
    referralName?: string;
    currentPromotionCode?: string;
    discountPercentage?: number;
    actualPrice?: number;
    currentComponentIndex?: number;
    taxDetails?: Record<string, number>;
}

// Define props with defaults
const props = withDefaults(defineProps<Props>(), {
    numLicenses: 0,
    freeLicenses: 0,
    taxPercentage: 0,
    referralName: "",
    currentPromotionCode: "",
    discountPercentage: 0,
    actualPrice: 0,
    currentComponentIndex: 0,
    taxDetails: () => ({})
});

// Define emits
const emit = defineEmits<{
    'quantity-did-update': [quantity: number, isValid: boolean];
    'promo-code-did-add': [promoCode: string, form: any];
    'net-price-did-update': [netPrice: number];
}>();

// Get validation schemas from parent component
const validationSchemas = inject('validationSchemas', {} as any);

// Form setup with Yup schema
const { handleSubmit, validate, setErrors } = useForm({
    validationSchema: validationSchemas.invoice || {},
});

// Reactive data
const promotionCode = ref("");
const quantity = ref(props.numLicenses);

// Field setup with validation from Yup schema
const {
    value: quantityField,
    meta: quantityMeta,
    errorMessage: quantityErrorMessage
} = useField('quantity', undefined, {
    initialValue: props.numLicenses
});

const {
    value: promoCodeField,
    meta: promoCodeMeta,
    errorMessage: promoCodeErrorMessage,
    setErrors: setPromoCodeErrors
} = useField('promotion_code', undefined, {
    initialValue: ""
});

const showQuantityError = computed(() => 
    quantityMeta.touched && quantityErrorMessage.value
);

const showPromoCodeError = computed(() => 
    (promoCodeMeta.touched || promoCodeErrorMessage.value) && promoCodeErrorMessage.value
);

watch(() => quantityField.value, (newVal) => {
    quantity.value = Number(newVal) || 0;
});

watch(() => promoCodeField.value, (newVal) => {
    promotionCode.value = newVal || "";
});

const freeLicenseTotal = computed(() => {
    return props.freeLicenses * props.actualPrice;
});

const totalPrice = computed(() => {
    return quantity.value * props.actualPrice;
});

const totalDiscount = computed(() => {
    if (quantity.value - props.freeLicenses <= 0) {
        return 0;
    }
    return (quantity.value - props.freeLicenses) * (props.actualPrice - discountPrice.value);
});

const discountPrice = computed(() => {
    if (!props.actualPrice) {
        return 0;
    }
    if (quantity.value - props.freeLicenses <= 0) {
        return props.actualPrice;
    }
    const discount = props.actualPrice * (props.discountPercentage / 100);
    return props.actualPrice - discount;
});

const subtotal = computed(() => {
    return !totalPrice.value ? 0 : totalPrice.value - freeLicenseTotal.value - totalDiscount.value;
});

const taxCharged = computed(() => {
    return ((parseFloat(props.taxPercentage.toString()) / 100) * subtotal.value);
});

const netPrice = computed(() => {
    return parseFloat((taxCharged.value + subtotal.value).toString());
});

const hasPromotion = computed(() => {
    return props.discountPercentage > 0 && props.currentPromotionCode.length > 0;
});

const formatCurrency = (value: number): string => {
    if (typeof window !== 'undefined' && (window as any).l?.locale === 'fr') {
        return `${parseFloat(value.toString()).toFixed(2).replace('.', ',')} $`;
    }
    return `$${parseFloat(value.toString()).toFixed(2)}`;
};

onMounted(() => {
    promotionCode.value = props.currentPromotionCode;
    promoCodeField.value = props.currentPromotionCode;
});

watch(() => props.numLicenses, (val) => {
    const newQuantity = parseInt(val.toString());
    quantity.value = isNaN(newQuantity) ? 0 : newQuantity;
    quantityField.value = quantity.value;
});

watch(() => props.currentPromotionCode, (newVal) => {
    promotionCode.value = newVal;
    promoCodeField.value = newVal;
});

watch(netPrice, (val) => {
    console.log(val);
    emit("net-price-did-update", val);
});

const formWillSubmit = handleSubmit((values) => {
});

const updateInvoice = async () => {
    const { valid } = await validate();
    if (valid) {
        emit("quantity-did-update", quantity.value, true);
    }
};

const applyPromoCode = async () => {
    if (!promotionCode.value || promotionCode.value.trim() === '') {
        return;
    }
    
    const formObject = {
        setErrors: (errors: Record<string, string | string[]>) => {
            if (errors.promotion_code) {
                setPromoCodeErrors(errors.promotion_code);
            }
        },
        validate
    };
    
    emit("promo-code-did-add", promotionCode.value, formObject);
};

defineExpose({
    formWillSubmit
});
</script>

<style>
table {
    border-collapse: separate;
}
</style>

